import { useMemo } from 'react';
import { debounce } from 'debounce';

import useLatestRef from 'common/hooks/useLatestRef';

type Callback = Parameters<typeof debounce>[0];

// Unfortunately `debounce` doesn't export a type for this. Clearly cleaner and
// easier to read to redeclare it than doing some type computation.
type DebouncedCallback<T> = T & {
  clear: () => void;
  flush: () => void;
};

const useDebouncedCallback = <T extends Callback>(
  callback: T,
  waitInMs: number,
): DebouncedCallback<T> => {
  const callbackRef = useLatestRef(callback);

  return useMemo(() => {
    return debounce(
      // @ts-expect-error - There is an error due to the inferred type of
      // `Callback` (`Function`). We just need to forward the arguments so no
      // need to add complicated types here. The hook is already properly typed
      // for consumers.
      (...args) => callbackRef.current(...args),
      waitInMs,
    );
  }, [callbackRef, waitInMs]);
};

export default useDebouncedCallback;
